// @flow
import React from 'react';
import { SuggestionItem } from '@finect/tabular-components/Suggestions';
import { Small } from '@finect/tabular-core/Small';
import { AlertCircle } from '@finect/tabular-icons/alertCircle';
import { keyMap } from '@finect/front-utils/keyMap';
import { finectAPI } from '@finect/front-utils/finectAPI';
import { debounce } from '@finect/front-utils/debounce';
import { cancelablePromise } from '@finect/front-utils/cancelablePromise';
import { AutocompleteBlock } from './AutocompleteBlock';
import { AutocompleteInput } from './AutocompleteInput';
import { Results, NoResults } from './Results';
import { Match } from './Match';

const placeholders = {
  initial: 'Ej.: vivienda, inversiones, hacienda, planificar...',
  default: 'Añade otro tema',
  error: 'No puedes añadir más temas'
};

const TAG_LIMIT = 5;

type Props = {
  handleAddTag: Function,
  removeLatestTag: Function,
  hasTags: number
};

type State = {
  suggestions: Array<Object>,
  showSuggestions: boolean,
  noResults: boolean,
  selectedSuggestion: number,
  placeholder: string
};

export class Autocomplete extends React.Component<Props, State> {
  input: any;
  pendingPromises: Array<any> = [];

  state = {
    suggestions: [],
    showSuggestions: false,
    noResults: false,
    selectedSuggestion: 0,
    placeholder: this.props.hasTags
      ? this.props.hasTags === TAG_LIMIT
        ? placeholders.error
        : placeholders.default
      : placeholders.initial
  };

  componentWillUnmount() {
    this.pendingPromises.map(promise => promise.cancel());
  }

  static getDerivedStateFromProps(props: Props, state: State) {
    const { placeholder } = state;

    if (props.hasTags === TAG_LIMIT) {
      return {
        placeholder: placeholders.error
      };
    }

    if (placeholder === placeholders.error) {
      return {
        placeholder: placeholders.default
      };
    }

    if (!props.hasTags && placeholder === placeholders.default) {
      return {
        placeholder: placeholders.initial
      };
    }

    return null;
  }

  clickedTag = () => {
    const { suggestions, selectedSuggestion } = this.state;
    if (selectedSuggestion === -1) return this.clearInput();
    const selectedTag = suggestions[selectedSuggestion];
    this.props.handleAddTag({
      id: selectedTag.uuid,
      name: selectedTag.text,
      description: '',
      following: false,
      stats: {
        questions: null,
        followers: null
      },
      synonyms: []
    });
    this.clearInput();
  };

  appendPendingPromise = (promise: cancelablePromise) => (this.pendingPromises = [...this.pendingPromises, promise]);

  removePendingPromise = (promise: cancelablePromise) => {
    const filteredPromises = this.pendingPromises.filter(pendingPromise => pendingPromise !== promise);
    this.pendingPromises = filteredPromises ? filteredPromises : [];
  };

  processPromise = (wrappedPromise: cancelablePromise) => {
    wrappedPromise.promise
      .then((search) => {
        if (search) {
          if (this.input.value === '') return this.hideSuggestions();
          this.setState({
            suggestions: search.slice(0, 10),
            showSuggestions: true,
            noResults: false
          });
        } else {
          this.setState({
            showSuggestions: true,
            noResults: true
          });
        }
      })
      .then(() => {
        this.removePendingPromise(wrappedPromise);
      })
      .catch((errorInfo) => {
        if (!errorInfo.isCanceled) {
          this.removePendingPromise(wrappedPromise);
        }
      });
  };

  fetchSuggestions = debounce(
    (searchTerm: string) =>
      finectAPI({
        version: 'v3',
        path: `tags/legacySearch?q=${searchTerm}`,
        method: 'GET',
        onSuccess: (search) => {
          if (search.length > 0) return search;
          return null;
        }
      }),
    300
  );

  getSuggestions = (searchTerm: string) => {
    if (searchTerm === '') return this.hideSuggestions();

    const wrappedPromise = cancelablePromise(this.fetchSuggestions(searchTerm));
    this.appendPendingPromise(wrappedPromise);
    this.processPromise(wrappedPromise);
  };

  clearInput = () => {
    this.input.value = '';
    this.hideSuggestions();
    setTimeout(() => this.input.focus(), 0);
  };

  hideSuggestions = () => {
    this.setState({
      showSuggestions: false,
      selectedSuggestion: -1
    });
  };

  handleDownArrow = () => {
    this.setState(state => ({
      selectedSuggestion: state.selectedSuggestion < state.suggestions.length - 1 ? state.selectedSuggestion + 1 : 0
    }));
  };

  handleUpArrow = () => {
    this.setState(state => ({
      selectedSuggestion: state.selectedSuggestion > 0 ? state.selectedSuggestion - 1 : state.suggestions.length - 1
    }));
  };

  handleBackspace = () => {
    if (this.input.value !== '') return;
    if (this.props.hasTags === TAG_LIMIT) {
      this.setState({ placeholder: placeholders.default });
    }
    this.props.removeLatestTag();
  };

  handleKeyDown = (event: SyntheticKeyboardEvent<HTMLElement>) => {
    switch (event.keyCode) {
      case keyMap.ENTER:
      case keyMap.RETURN:
        this.handleFocus();
        this.clickedTag();
        break;
      case keyMap.UP:
        event.preventDefault();
        this.handleUpArrow();
        break;
      case keyMap.DOWN:
        event.preventDefault();
        this.handleDownArrow();
        break;
      case keyMap.ESCAPE:
        this.hideSuggestions();
        this.setState({
          selectedSuggestion: -1
        });
        break;
      case keyMap.BACKSPACE:
        this.handleBackspace();
        break;
      default:
        break;
    }
  };

  setSelectedSuggestion = (index: number) => {
    this.setState({
      selectedSuggestion: index
    });
  };

  handleChange = () => {
    if (this.props.hasTags < TAG_LIMIT) {
      this.getSuggestions(this.input.value);
    }
  };

  handleFocus = () => {
    if (this.props.hasTags < TAG_LIMIT) {
      this.setState({
        showSuggestions: true
      });
    }
  };

  handleBlur = () => {
    if (this.props.hasTags < TAG_LIMIT) {
      this.setState({
        showSuggestions: false,
        selectedSuggestion: 0,
        placeholder: this.props.hasTags ? placeholders.default : placeholders.initial
      });
    }
  };

  render() {
    const { suggestions, selectedSuggestion, showSuggestions, noResults, placeholder } = this.state;
    const showResults = suggestions.length > 0 && showSuggestions;
    return (
      <AutocompleteBlock>
        <AutocompleteInput
          ref={el => (this.input = el)}
          placeholder={placeholder}
          onKeyDown={this.handleKeyDown}
          onChange={this.handleChange}
          onFocus={this.handleFocus}
          onBlur={this.handleBlur}
          disabled={this.props.hasTags === TAG_LIMIT}
        />
        {showResults ? (
          <Results selectedIndex={selectedSuggestion} resultCount={suggestions.length}>
            {noResults ? (
              <NoResults>
                <AlertCircle size="18" /> <Small>Vaya, no hay ningún tema que coincida con tu búsqueda.</Small>
              </NoResults>
            ) :
              suggestions.map((tag, index) => (
                <SuggestionItem
                  key={tag.uuid}
                  isSelected={selectedSuggestion === index}
                  index={index}
                  setSelectedSuggestion={this.setSelectedSuggestion}
                  clickedSuggestion={this.clickedTag}
                >
                  {tag.hit === 'synonym' ? (
                    <Match>
                      {tag.text}
                      <br />
                      <Small>Las preguntas sobre &quot;{tag.synonym}&quot; suelen ir bajo este tema.</Small>
                    </Match>
                  ) :
                    tag.text
                  }
                </SuggestionItem>
              ))
            }
          </Results>
        ) : null}
      </AutocompleteBlock>
    );
  }
}
